import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../../contexts/LanguageContext';
import { useAuth } from '../../contexts/AuthContext';
import { useTranslation } from 'react-i18next';
import { getDrivers, getDriverStats, deleteDriver } from '../../services/driverService';
import { Driver, DriverStats } from '../../types/driver';
import {
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Paper,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
} from '@mui/material';
import { Edit, Delete, Add } from '@mui/icons-material';
import DriverStatusUpdate from '../../components/drivers/DriverStatusUpdate';

const DriversList: React.FC = () => {
  const { t } = useLanguage();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [drivers, setDrivers] = useState<Driver[] | null>(null);
  const [stats, setStats] = useState<DriverStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedDriverId, setSelectedDriverId] = useState<string | null>(null);
  const [currentDriver, setCurrentDriver] = useState<Driver | null>(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const [driversData, statsData] = await Promise.all([
          getDrivers(),
          getDriverStats(),
        ]);
        setDrivers(driversData);
        setStats(statsData);
        
        // If current user is a driver, find their driver record
        if (user?.role === 'driver' && user?.id) {
          const currentDriverRecord = driversData.find(driver => driver.id === user.id.toString());
          setCurrentDriver(currentDriverRecord || null);
        }
      } catch (error) {
        console.error('Error fetching drivers:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const handleEdit = (id: string) => {
    navigate(`/drivers/edit/${id}`);
  };

  const handleDeleteClick = (id: string) => {
    setSelectedDriverId(id);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!selectedDriverId || !drivers) return;
    
    try {
      await deleteDriver(selectedDriverId);
      setDrivers(drivers.filter(driver => driver.id !== selectedDriverId));
      setDeleteDialogOpen(false);
    } catch (error) {
      console.error('Error deleting driver:', error);
    }
  };

  const handleStatusUpdate = (updatedDriver: Driver) => {
    // Update the current driver state
    setCurrentDriver(updatedDriver);
    
    // Update the driver in the drivers list
    if (drivers) {
      setDrivers(drivers.map(driver => 
        driver.id === updatedDriver.id ? updatedDriver : driver
      ));
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'Active':
        return 'success';
      case 'Inactive':
        return 'default';
      case 'On Delivery':
        return 'primary';
      default:
        return 'default';
    }
  };
  
  const getStatusTranslation = (status: string) => {
    // Map the status to our translation keys
    switch (status) {
      case 'Active':
        return t('drivers.statuses.active');
      case 'Inactive':
        return t('drivers.statuses.inactive');
      case 'On Delivery':
        return t('drivers.statuses.on_delivery');
      default:
        return status; // Return as is if no translation found
    }
  };

  if (loading || drivers === null) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4">{t('drivers.title')}</Typography>
        {user?.role === 'admin' && (
          <Button
            variant="contained"
            color="primary"
            startIcon={<Add />}
            onClick={() => navigate('/drivers/new')}
          >
            {t('drivers.addDriver')}
          </Button>
        )}
      </Box>

      {/* Driver Status Update - Only show for driver users */}
      {user?.role === 'driver' && currentDriver && (
        <DriverStatusUpdate
          currentDriver={currentDriver}
          onStatusUpdate={handleStatusUpdate}
        />
      )}

      {stats && (
        <Box display="flex" gap={2} mb={3} flexWrap="wrap">
          <StatCard
            title={t('drivers.stats.total')}
            value={stats.total}
            color="primary"
          />
          <StatCard
            title={t('drivers.stats.active')}
            value={stats.active}
            color="success"
          />
          <StatCard
            title={t('drivers.stats.onDelivery')}
            value={stats.onDelivery}
            color="info"
          />
          <StatCard
            title={t('drivers.stats.inactive')}
            value={stats.inactive}
            color="warning"
          />
        </Box>
      )}

      <Card>
        <CardContent>
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>{t('drivers.name')}</TableCell>
                  <TableCell>{t('drivers.email')}</TableCell>
                  <TableCell>{t('drivers.phone')}</TableCell>
                  <TableCell>{t('drivers.status')}</TableCell>
                  <TableCell>{t('drivers.vehicleType')}</TableCell>
                  <TableCell>{t('drivers.vehicleNumber')}</TableCell>
                  {user?.role === 'admin' && <TableCell>{t('common.actions')}</TableCell>}
                </TableRow>
              </TableHead>
              <TableBody>
                {drivers.map((driver) => (
                  <TableRow key={driver.id} hover>
                    <TableCell>{driver.name}</TableCell>
                    <TableCell>{driver.email}</TableCell>
                    <TableCell>{driver.phone || '-'}</TableCell>
                    <TableCell>
                      <Chip 
                        label={getStatusTranslation(driver.status)}
                        color={getStatusColor(driver.status)}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>{driver.vehicleType || '-'}</TableCell>
                    <TableCell>{driver.vehicleNumber || '-'}</TableCell>
                    {user?.role === 'admin' && (
                      <TableCell>
                        <IconButton onClick={() => handleEdit(driver.id)} size="small">
                          <Edit fontSize="small" />
                        </IconButton>
                        <IconButton 
                          onClick={() => handleDeleteClick(driver.id)}
                          size="small"
                          color="error"
                        >
                          <Delete fontSize="small" />
                        </IconButton>
                      </TableCell>
                    )}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      <DeleteDialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        onConfirm={handleDeleteConfirm}
        title={t('drivers.deleteDialog.title')}
        content={t('drivers.deleteDialog.content')}
      />
    </Box>
  );
};

const StatCard = ({ title, value, color }: { title: string; value: number; color: string }) => (
  <Card sx={{ minWidth: 200, flex: 1 }}>
    <CardContent>
      <Typography color="textSecondary" gutterBottom>
        {title}
      </Typography>
      <Typography variant="h5" component="div" color={color}>
        {value}
      </Typography>
    </CardContent>
  </Card>
);

const DeleteDialog = ({
  open,
  onClose,
  onConfirm,
  title,
  content,
}: {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
  title: string;
  content: string;
}) => {
  const { t } = useTranslation();
  
  return (
    <Dialog open={open} onClose={onClose}>
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>
        <DialogContentText>{content}</DialogContentText>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="primary">
          {t('common.cancel')}
        </Button>
        <Button onClick={onConfirm} color="error" variant="contained">
          {t('crud.delete')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default DriversList;
